import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useSuccessionTheme } from './SuccessionThemeProvider';
import FormField from './BCPFormField';

const QuestionPanel = ({
  section,
  formData,
  onFormDataChange,
  onNext,
  onPrevious,
  onFinalize,
  isFirstSection,
  isLastSection,
  colors,
  themeColors
}) => {
  const { theme, isLightTheme, isDarkTheme, company } = useSuccessionTheme();
  
  // Use passed colors or fallback to theme context
  const actualColors = colors || {
    primary: company?.primary_color || '#5489B6',
    secondary: company?.secondary_color || '#002949',
    accent: company?.accent_color || '#5B5E5A',
    neutral: company?.neutral_color || '#666666',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
  };

  const actualThemeColors = themeColors || {
    bgPrimary: isDarkTheme ? '#1f2937' : '#ffffff',
    bgSecondary: isDarkTheme ? '#111827' : '#f9fafb',
    bgTertiary: isDarkTheme ? '#374151' : '#e5e7eb',
    textPrimary: isDarkTheme ? '#f9fafb' : '#111827',
    textSecondary: isDarkTheme ? '#d1d5db' : '#6b7280',
    textMuted: isDarkTheme ? '#9ca3af' : '#9ca3af',
    border: isDarkTheme ? '#374151' : '#e5e7eb',
    shadow: isDarkTheme ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  };
  
  const handleFieldChange = (fieldId, value) => {
    // Update only the section's data
    const sectionData = {
      ...(formData[section.id] || {}),
      [fieldId]: value
    };
    onFormDataChange(sectionData);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <h2 
          className="text-xl font-semibold transition-colors duration-300"
          style={{ color: actualThemeColors.textPrimary }}
        >
          {section.title}
        </h2>
        <p 
          className="mt-2 text-sm transition-colors duration-300"
          style={{ color: actualThemeColors.textSecondary }}
        >
          {section.description}
        </p>
        {section.helpText && (
          <p 
            className="mt-2 text-sm transition-colors duration-300"
            style={{ color: actualColors.primary }}
          >
            {section.helpText}
          </p>
        )}
      </div>

      <div className="space-y-6">
        {section.fields.map((field) => (
          <FormField
            key={field.id}
            field={field}
            value={formData[section.id]?.[field.id]}
            onChange={(value) => handleFieldChange(field.id, value)}
            colors={actualColors}
            themeColors={actualThemeColors}
          />
        ))}
      </div>

      <div className="mt-8 flex justify-between">
        <button
          type="button"
          onClick={onPrevious}
          disabled={isFirstSection}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm transition-colors duration-300"
          style={{
            backgroundColor: isFirstSection ? actualThemeColors.bgTertiary : actualColors.primary,
            color: isFirstSection ? actualThemeColors.textMuted : '#ffffff',
            cursor: isFirstSection ? 'not-allowed' : 'pointer',
            opacity: isFirstSection ? 0.6 : 1
          }}
        >
          <ChevronLeftIcon className="h-5 w-5 mr-2" />
          Previous
        </button>
        <button
          type="button"
          onClick={isLastSection ? onFinalize : onNext}
          disabled={false} // Never disable - handle differently for last section
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm transition-colors duration-300"
          style={{
            backgroundColor: actualColors.primary,
            color: '#ffffff',
            cursor: 'pointer',
            opacity: 1
          }}
          onMouseEnter={(e) => {
            e.target.style.opacity = '0.9';
          }}
          onMouseLeave={(e) => {
            e.target.style.opacity = '1';
          }}
        >
          {isLastSection ? 'Finalize BCP' : 'Next'}
          <ChevronRightIcon className="h-5 w-5 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default QuestionPanel; 